import { Injectable } from '@angular/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Platform } from '@ionic/angular';
import { Device } from '@capacitor/device';

export interface TwoNCredentials {
  username: string;
  password: string;
  deviceId?: string;
  url?: string;
  type?: 'user' | 'device' | 'credentials_service';
}

@Injectable({
  providedIn: 'root'
})
export class CredentialsService {
  private readonly TWON_USERNAME_KEY = 'twon_username';
  private readonly TWON_PASSWORD_KEY = 'twon_password';
  private readonly TWON_DEVICE_ID_KEY = 'twon_device_id';
  private readonly TWON_URL_KEY = 'twon_url';
  private readonly TWON_TYPE_KEY = 'twon_type';
  
  private isPlatformCompatible = false;
  private platformCheckCompleted = false;

  // Deduplication: track last stored credentials to prevent redundant storage
  private lastStoredCredentialsHash: string | null = null;
  private isStoring = false;

  constructor(private platform: Platform) {
    this.checkPlatformCompatibility();
  }

  /**
   * Check platform compatibility once and cache the result
   */
  private async checkPlatformCompatibility(): Promise<void> {
    if (this.platformCheckCompleted) {
      return;
    }

    try {
      // Must be running on Capacitor (not web)
      if (!this.platform.is('capacitor')) {
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
        return;
      }

      // Must be iOS or Android
      if (!this.platform.is('ios') && !this.platform.is('android')) {
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
        return;
      }

      // Must not be running on simulator/emulator
      try {
        const deviceInfo = await Device.getInfo();
        this.isPlatformCompatible = !deviceInfo.isVirtual;
      } catch (error) {
        // If we can't get device info, assume it's not compatible
        this.isPlatformCompatible = false;
      }
    } catch (error) {
      this.isPlatformCompatible = false;
    }

    this.platformCheckCompleted = true;
  }

  /**
   * Wait for platform check to complete
   */
  private async waitForPlatformCheck(): Promise<void> {
    // If already completed, return immediately
    if (this.platformCheckCompleted) {
      return;
    }

    // Wait up to 2 seconds for platform check
    const maxWaitTime = 2000;
    const checkInterval = 50;
    let waited = 0;

    while (!this.platformCheckCompleted && waited < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }

    if (!this.platformCheckCompleted) {
      console.warn('Platform check timeout - assuming incompatible');
      this.isPlatformCompatible = false;
      this.platformCheckCompleted = true;
    }
  }

  /**
   * Store 2N credentials securely
   */
  async storeTwoNCredentials(credentials: TwoNCredentials): Promise<boolean> {
    // Wait for platform check to complete
    await this.waitForPlatformCheck();

    // Platform killswitch - only store on physical devices
    if (!this.isPlatformCompatible) {
      console.debug('2N credentials storage skipped - platform not compatible (web/simulator)');
      return true; // Return true to prevent errors in calling code
    }

    // Create a hash of credentials to detect duplicates
    const credentialsHash = `${credentials.username}:${credentials.password}:${credentials.deviceId || ''}`;

    // Skip if same credentials were just stored
    if (this.lastStoredCredentialsHash === credentialsHash) {
      return true; // Already stored
    }

    // Prevent concurrent storage operations
    if (this.isStoring) {
      return true; // Storage in progress
    }

    this.isStoring = true;

    try {
      console.debug('Storing 2N credentials securely...');

      await Promise.all([
        SecureStoragePlugin.set({ key: this.TWON_USERNAME_KEY, value: credentials.username }),
        SecureStoragePlugin.set({ key: this.TWON_PASSWORD_KEY, value: credentials.password }),
        SecureStoragePlugin.set({ key: this.TWON_DEVICE_ID_KEY, value: credentials.deviceId || '' }),
        SecureStoragePlugin.set({ key: this.TWON_URL_KEY, value: credentials.url || 'https://my2n.com' }),
        SecureStoragePlugin.set({ key: this.TWON_TYPE_KEY, value: credentials.type || 'user' })
      ]);

      console.debug('2N credentials stored successfully');
      this.lastStoredCredentialsHash = credentialsHash;
      this.isStoring = false;
      return true;
    } catch (error) {
      console.error('Error storing 2N credentials:', error);
      this.isStoring = false;
      return false;
    }
  }

  /**
   * Retrieve 2N credentials from secure storage
   */
  async getTwoNCredentials(): Promise<TwoNCredentials | null> {
    // Wait for platform check to complete
    await this.waitForPlatformCheck();
    
    // Platform killswitch - only retrieve on physical devices
    if (!this.isPlatformCompatible) {
      // console.log('2N credentials retrieval skipped - platform not compatible (web/simulator)');
      return null;
    }

    try {
      console.debug('Retrieving 2N credentials from secure storage...');
      
      const [username, password, deviceId, url, type] = await Promise.all([
        SecureStoragePlugin.get({ key: this.TWON_USERNAME_KEY }),
        SecureStoragePlugin.get({ key: this.TWON_PASSWORD_KEY }),
        SecureStoragePlugin.get({ key: this.TWON_DEVICE_ID_KEY }),
        SecureStoragePlugin.get({ key: this.TWON_URL_KEY }),
        SecureStoragePlugin.get({ key: this.TWON_TYPE_KEY })
      ]);

      if (!username?.value || !password?.value) {
        console.log('No 2N credentials found in secure storage');
        return null;
      }

      const credentials: TwoNCredentials = {
        username: username.value,
        password: password.value,
        deviceId: deviceId?.value || undefined,
        url: url?.value || 'https://my2n.com',
        type: (type?.value as 'user' | 'device' | 'credentials_service') || 'user'
      };

      console.debug('2N credentials retrieved successfully');
      return credentials;
    } catch (error) {
      console.error('Error retrieving 2N credentials:', error);
      return null;
    }
  }

  /**
   * Check if 2N credentials exist in secure storage
   */
  async hasTwoNCredentials(): Promise<boolean> {
    try {
      const credentials = await this.getTwoNCredentials();
      return credentials !== null;
    } catch (error) {
      console.error('Error checking for 2N credentials:', error);
      return false;
    }
  }

  /**
   * Clear 2N credentials from secure storage
   */
  async clearTwoNCredentials(): Promise<boolean> {
    // Wait for platform check to complete
    await this.waitForPlatformCheck();
    
    // Platform killswitch - only clear on physical devices
    if (!this.isPlatformCompatible) {
      console.log('2N credentials clear skipped - platform not compatible (web/simulator)');
      return true;
    }

    try {
      console.log('Clearing 2N credentials from secure storage...');
      
      await Promise.all([
        SecureStoragePlugin.remove({ key: this.TWON_USERNAME_KEY }),
        SecureStoragePlugin.remove({ key: this.TWON_PASSWORD_KEY }),
        SecureStoragePlugin.remove({ key: this.TWON_DEVICE_ID_KEY }),
        SecureStoragePlugin.remove({ key: this.TWON_URL_KEY }),
        SecureStoragePlugin.remove({ key: this.TWON_TYPE_KEY })
      ]);

      console.log('2N credentials cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing 2N credentials:', error);
      return false;
    }
  }

  /**
   * Update specific credential fields
   */
  async updateTwoNCredentials(updates: Partial<TwoNCredentials>): Promise<boolean> {
    try {
      const existing = await this.getTwoNCredentials();
      if (!existing) {
        console.error('No existing credentials to update');
        return false;
      }

      const updated: TwoNCredentials = {
        ...existing,
        ...updates
      };

      return await this.storeTwoNCredentials(updated);
    } catch (error) {
      console.error('Error updating 2N credentials:', error);
      return false;
    }
  }
} 